import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Compass, MapPin, Briefcase, ChevronDown, ClipboardList, Home as HomeIcon, Info, Instagram, Facebook, Youtube, MessageSquarePlus } from "lucide-react";
import RazorpayTrustBadge from "@/components/RazorpayTrustBadge";
import SSLVerifiedBadge from "@/components/SSLVerifiedBadge";
import AuthModal from "../components/AuthModal";
import EnquiryModal from "../components/EnquiryModal";
import { useNavigate, Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { trackVisit } from "../utils/tracker";

// ── Cinematic hero reel (Iter 60) ────────────────────────────────────
// 3 licensed 4K Pexels clips looped seamlessly as a muted background hero.
// Each clip auto-advances after playback (see onEnded handler below) so we
// get a "commercial" feel without needing a real video editor. Poster
// images preload the tone before the MP4 downloads on slow connections.
const heroVideos = [
  {
    src: "https://videos.pexels.com/video-files/2098989/2098989-hd_1920_1080_30fps.mp4",
    poster: "https://images.pexels.com/photos/3881106/pexels-photo-3881106.jpeg?auto=compress&cs=tinysrgb&w=1920",
    label: "Aerial cinematic",
  },
  {
    src: "https://videos.pexels.com/video-files/1093662/1093662-hd_1920_1080_30fps.mp4",
    poster: "https://images.pexels.com/photos/36647370/pexels-photo-36647370.jpeg?auto=compress&cs=tinysrgb&w=1920",
    label: "Landscape drone",
  },
  {
    src: "https://videos.pexels.com/video-files/3571264/3571264-hd_1920_1080_30fps.mp4",
    poster: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1920",
    label: "Cinematic travel",
  },
];

const heroImages = [
  {
    url: "https://images.pexels.com/photos/3881106/pexels-photo-3881106.jpeg?auto=compress&cs=tinysrgb&w=1920",
    alt: "Taj Mahal"
  },
  {
    url: "https://images.pexels.com/photos/36647370/pexels-photo-36647370.jpeg?auto=compress&cs=tinysrgb&w=1920",
    alt: "Kerala Backwaters"
  },
  {
    url: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1920",
    alt: "Goa Beaches"
  },
  {
    url: "https://images.unsplash.com/photo-1599661046289-e31897846e41?w=1920",
    alt: "Jaipur"
  }
];

function FadeInSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

export default function LandingPage({ isAuthenticated, setIsAuthenticated, setUser, openEnquiryOnMount = false }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  // If the browser/network can't load the video (blocked autoplay, slow
  // 3G, etc.), we transparently fall back to the existing image slideshow
  // so the page never renders a black hero.
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const navigate = useNavigate();
  const aboutRef = useRef(null);

  useEffect(() => {
    trackVisit("landing");
    // Only cycle the image slideshow when the video reel isn't in use
    // (either explicitly failed OR simply hasn't loaded yet).
    if (!videoFailed) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [videoFailed]);

  // Iter 70 — auto-open the enquiry modal when the user lands on
  // `/enquiry` OR opens `/?enquiry=1`, so we get a clean shareable URL
  // for social posts (`https://travelsate.in/enquiry`).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (openEnquiryOnMount || params.get("enquiry") === "1") {
      setShowEnquiry(true);
    }
  }, [openEnquiryOnMount]);

  // Advance to next video whenever the current one finishes playing —
  // creates a seamless multi-scene reel without JS timers (which would
  // desync when the tab is backgrounded).
  const handleVideoEnded = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % heroVideos.length);
  };

  const [authPurpose, setAuthPurpose] = useState(null); // null, "bookings"

  const handleGetStarted = () => {
    navigate("/booking");
  };

  const handleViewBookings = () => {
    if (isAuthenticated) {
      fetchAndShowBookings();
    } else {
      setAuthPurpose("bookings");
      setShowAuth(true);
    }
  };

  const fetchAndShowBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      if (response.data && response.data.length > 0) {
        navigate("/booking", { state: { showBookings: true } });
      } else {
        toast.info("No bookings found. Start planning your next trip!", { duration: 4000 });
        navigate("/booking");
      }
    } catch {
      toast.error("Unable to fetch bookings. Please try again.");
    }
  };

  const handleAuthSuccess = () => {
    setShowAuth(false);
    if (authPurpose === "bookings") {
      setAuthPurpose(null);
      // Small delay to ensure token is persisted before API call
      setTimeout(() => fetchAndShowBookings(), 300);
    }
  };

  const scrollToAbout = () => {
    aboutRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="w-full overflow-y-auto" data-testid="landing-page">
      {/* Top Navigation Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 flex items-center justify-between h-14">
          <img src="/logo.svg" alt="Travelsate" className="h-7" />
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              data-testid="nav-home-button"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full text-white/70 text-sm hover:bg-white/10 hover:text-white transition-all"
            >
              <HomeIcon className="w-3.5 h-3.5" />
              Home
            </button>
            <button
              data-testid="nav-about-button"
              onClick={() => navigate("/about")}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full text-white/70 text-sm hover:bg-white/10 hover:text-white transition-all"
            >
              <Info className="w-3.5 h-3.5" />
              About Us
            </button>
            <button
              data-testid="nav-enquiry-button"
              onClick={() => setShowEnquiry(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#E5C468] transition-all"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Submit an Enquiry</span>
              <span className="sm:hidden">Enquiry</span>
            </button>
            <button
              data-testid="view-bookings-button"
              onClick={handleViewBookings}
              className="flex items-center gap-1.5 px-3 py-2 text-white/70 text-sm hover:bg-white/10 hover:text-white transition-all rounded-md"
            >
              <ClipboardList className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">My Bookings</span>
              <span className="sm:hidden">Bookings</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── "How It Works" journey infographic (Iter 61 · moved to top in
          Iter 62 · re-themed to match dark aesthetic in Iter 63) ─────────
          Same content as before, now on the site's canonical dark
          background (`#050508`) so it visually chains with the About /
          Trust / Footer sections instead of feeling like a light-mode
          island. The illustration itself keeps its paper look — we frame
          it with a subtle warm-cream card so the transition from dark
          section → light illustration feels intentional (not accidental). */}
      <section className="relative bg-[#050508] py-28 sm:py-40 px-6" data-testid="how-it-works-section">
        {/* Subtle warm gold glow behind the illustration to soften the
            hard cream-on-black contrast. */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.06),transparent_60%)] pointer-events-none" />
        <div className="relative w-full mx-auto text-center">
          <FadeInSection>
            <p className="text-xs tracking-[0.25em] uppercase font-medium text-[#D4AF37]/70 mb-3">
              How Travelsate Works
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4 leading-snug">
              Your journey, <span className="text-[#D4AF37] italic">on your terms</span>
            </h2>
            <p className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto mb-10">
              From dreaming at home to exploring the world and back —
              every step covered, every rupee flexible.
            </p>
          </FadeInSection>
          <FadeInSection delay={0.15}>
            {/* Iter 66 — User feedback: infographic felt too busy at 80%
                width. Shrunk to 65% of viewport (sm:w-[65%]) and increased
                the section's vertical padding so there's more dark
                breathing room framing the illustration. Icons render
                smaller but the overall composition feels calmer. */}
            <div className="relative mx-auto w-11/12 sm:w-[65%] py-4" data-testid="journey-infographic-wrap">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_wanderlust-india-49/artifacts/cfj8s5p5_5038474.png"
                alt="Travelsate journey — Home, Plan, Book, Fly, Stay, Explore, Home. Easy interest-free installments. Book now, pay later."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="journey-infographic"
              />
            </div>
          </FadeInSection>
          <FadeInSection delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-4">
              <Button
                onClick={handleGetStarted}
                className="bg-[#D4AF37] text-black hover:bg-[#E5C468] font-medium px-8 py-5"
                data-testid="how-it-works-cta"
              >
                Start planning your trip
              </Button>
              <button
                onClick={() => aboutRef.current?.scrollIntoView({ behavior: "smooth" })}
                className="text-white/70 hover:text-white text-sm underline underline-offset-4 decoration-[#D4AF37]/40"
                data-testid="how-it-works-learn-more"
              >
                Learn more about us
              </button>
              {/* NRI-focused enquiry CTA — no login needed. Positioned as a
                  softer alternative for prospects who aren't ready to build
                  a full wizard quote themselves. */}
              <div className="mt-6 flex flex-col items-center gap-2">
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]/70">Coming home from abroad?</p>
                <button
                  onClick={() => setShowEnquiry(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/40 text-white/90 hover:text-white hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 text-sm transition-all"
                  data-testid="how-it-works-enquiry-cta"
                >
                  <MessageSquarePlus className="w-4 h-4 text-[#D4AF37]" />
                  Submit an enquiry — we'll email you an EMI plan
                </button>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* Hero Section — Cinematic video reel (Iter 60) */}
      <div className="relative w-full h-screen overflow-hidden" data-testid="hero-video-section">
        {/* Video layer — muted autoplay loop of licensed 4K Pexels clips.
            Falls back to the still-image slideshow if any video fails. */}
        {!videoFailed && (
          <AnimatePresence mode="wait">
            <motion.video
              key={currentVideoIndex}
              ref={videoRef}
              src={heroVideos[currentVideoIndex].src}
              poster={heroVideos[currentVideoIndex].poster}
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={handleVideoEnded}
              onError={() => setVideoFailed(true)}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-video"
            />
          </AnimatePresence>
        )}
        {videoFailed && (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full"
            >
              <div
                className="absolute inset-0 w-full h-full bg-cover bg-center"
                style={{ backgroundImage: `url(${heroImages[currentImageIndex].url})` }}
              />
            </motion.div>
          </AnimatePresence>
        )}
        {/* Dark-to-transparent gradient + subtle warm gold vignette for
            the Apple-commercial aesthetic. Keeps the CTA readable regardless
            of which clip is playing. */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-black/50 to-black/30" />
        {/* Iter 67 — Smooth the hard split between the black "How it Works"
            section above and the video below. This top-fade blends the
            first ~35% of the video into the section's dark background so
            the transition feels cinematic instead of a hard cut. */}
        <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-[#050508] via-[#050508]/70 to-transparent pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(212,175,55,0.08),transparent_60%)]" />

        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <img src="/logo.svg" alt="Travelsate" className="h-12 mx-auto mb-4" data-testid="brand-logo" />
            {/* "Book Now, Pay Later" pill — glassmorphism, sits above the
                brand tagline as the marquee value prop. */}
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full backdrop-blur-md bg-white/5 border border-[#D4AF37]/30"
              data-testid="hero-emi-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
              <p className="text-[#D4AF37] text-[11px] tracking-[0.25em] uppercase font-semibold">
                Book Now · Pay Later
              </p>
            </div>
            <p className="text-xs tracking-[0.2em] uppercase font-medium text-[#D4AF37] mb-6">
              Discover Nature's Hidden Treasures
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium text-white mb-6">
              Journey Beyond
              <br />
              the Ordinary
            </h1>
            <p className="text-base font-light tracking-wide text-white/80 max-w-2xl mx-auto mb-12">
              Curated luxury experiences across India and the world's most breathtaking destinations.
              <br />
              Your extraordinary adventure begins here.
            </p>
            <Button
              data-testid="lets-go-button"
              onClick={handleGetStarted}
              className="bg-[#D4AF37] text-black font-medium px-12 py-6 text-lg hover:bg-[#E5C468] transition-colors"
            >
              Let's Go
            </Button>
          </motion.div>
        </div>

        {/* Video reel progress dots — sits above the scroll indicator so
            visitors know a multi-scene reel is playing. Hidden on the image
            fallback path since it wouldn't make sense there. */}
        {!videoFailed && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid="hero-video-progress">
            {heroVideos.map((_, i) => (
              <span
                key={i}
                className={`h-[3px] rounded-full transition-all duration-500 ${
                  i === currentVideoIndex ? "w-8 bg-[#D4AF37]" : "w-4 bg-white/25"
                }`}
              />
            ))}
          </div>
        )}

        {/* Scroll indicator */}
        <motion.button
          onClick={scrollToAbout}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-[#D4AF37] transition-colors cursor-pointer"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          data-testid="scroll-down-button"
          aria-label="Scroll to about section"
        >
          <ChevronDown className="w-8 h-8" />
        </motion.button>
      </div>

      {/* About Section */}
      <section ref={aboutRef} className="relative bg-[#050508] py-24 sm:py-32 px-6" data-testid="about-section">
        <div className="max-w-4xl mx-auto">
          <FadeInSection>
            <p className="text-xs tracking-[0.25em] uppercase font-medium text-[#D4AF37]/70 text-center mb-4">
              About Travelsate
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white text-center mb-4 leading-snug">
              Where smart planning meets
              <br />
              <span className="text-[#D4AF37]">soulful travel</span>
            </h2>
            <div className="w-16 h-px bg-[#D4AF37]/40 mx-auto mb-12" />
          </FadeInSection>

          <div className="space-y-8">
            <FadeInSection delay={0.1}>
              <p className="text-white/70 leading-relaxed text-base sm:text-lg text-center max-w-3xl mx-auto">
                At Travelsate, we merge intelligent technology with human insight to craft trips that do more than take you places — they sate your desire to explore.
              </p>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <p className="text-white/60 leading-relaxed text-base text-center max-w-3xl mx-auto">
                Our digital platform simplifies every stage of travel planning — from discovering destinations to building custom itineraries, booking stays, and managing experiences — all in one seamless flow.
              </p>
            </FadeInSection>

            {/* Feature pillars */}
            <FadeInSection delay={0.3}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 my-14">
                <div className="text-center p-6 rounded-lg border border-white/5 bg-white/[0.02]">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <Compass className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white font-medium text-sm mb-2">Smart Discovery</h3>
                  <p className="text-white/50 text-xs leading-relaxed">Data-driven suggestions tailored to your style, pace, and purpose</p>
                </div>
                <div className="text-center p-6 rounded-lg border border-white/5 bg-white/[0.02]">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white font-medium text-sm mb-2">Local Expertise</h3>
                  <p className="text-white/50 text-xs leading-relaxed">Every journey reflects authentic, curated local knowledge</p>
                </div>
                <div className="text-center p-6 rounded-lg border border-white/5 bg-white/[0.02]">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white font-medium text-sm mb-2">Complete Packaging</h3>
                  <p className="text-white/50 text-xs leading-relaxed">Doorstep departure to homecoming, everything handled</p>
                </div>
              </div>
            </FadeInSection>

            <FadeInSection delay={0.4}>
              <p className="text-white/60 leading-relaxed text-base text-center max-w-3xl mx-auto">
                Behind every suggestion lies smart data and local expertise, ensuring each journey reflects your style, your pace, and your purpose. Whether you're chasing mountain air, beach sunsets, or cultural escapes, Travelsate curates your adventure so that when you return, you feel completely fulfilled.
              </p>
            </FadeInSection>

            <FadeInSection delay={0.5}>
              <div className="border border-[#D4AF37]/20 rounded-lg p-8 mt-10 text-center bg-[#D4AF37]/[0.03]">
                <p className="text-white/80 leading-relaxed text-base sm:text-lg italic font-serif">
                  Experience seamless travel like never before — we package your entire vacation, from doorstep departure to homecoming.
                </p>
              </div>
            </FadeInSection>

            <FadeInSection delay={0.6}>
              <p className="text-white/50 text-sm text-center mt-10 tracking-wide">
                Because travel shouldn't just be easy — it should be enriching, effortless, and entirely satisfying.
              </p>
            </FadeInSection>

            <FadeInSection delay={0.7}>
              <div className="text-center mt-12">
                <Button
                  data-testid="about-lets-go-button"
                  onClick={handleGetStarted}
                  className="bg-[#D4AF37] text-black font-medium px-10 py-5 text-base hover:bg-[#E5C468] transition-colors"
                >
                  Start Your Journey
                </Button>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* ─── Trust certifications (Razorpay + SSL) ───────────────────────
          Two-card grid above the footer — Razorpay handles payment trust,
          SSL handles transport/site trust. Together they cover the two
          pillars Indian travel customers vet before committing money. */}
      <section className="border-t border-white/10 bg-black/80 py-10" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RazorpayTrustBadge variant="full" />
            <SSLVerifiedBadge variant="full" />
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-black/70 py-8 mt-0" data-testid="site-footer">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()} Travelsate. All rights reserved.
          </p>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <Link to="/privacy" className="text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/terms" className="text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/refund-policy" className="text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="footer-refund-link">Refund &amp; Cancellation</Link>
            <a href="mailto:admin@travelsate.in" className="text-white/60 hover:text-[#D4AF37] transition-colors">admin@travelsate.in</a>
            <a href="tel:+919972744308" className="text-white/60 hover:text-[#D4AF37] transition-colors" data-testid="landing-footer-phone">+91 99727 44308</a>
            {/* Instagram link — uses Insta's brand-recognisable purple→pink→
                orange gradient on hover so it reads as a social link
                without an explicit "Instagram" label cluttering the strip. */}
            <a
              href="https://www.instagram.com/travelsate_letsgo/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Travelsate on Instagram"
              data-testid="footer-instagram-link"
              className="group inline-flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/15 group-hover:border-transparent group-hover:bg-gradient-to-br group-hover:from-[#feda75] group-hover:via-[#d62976] group-hover:to-[#4f5bd5] transition-all">
                <Instagram className="w-3.5 h-3.5" />
              </span>
              <span className="hidden sm:inline text-[11px] tracking-wide">Instagram</span>
            </a>
            {/* Facebook link — Facebook's brand blue (#1877F2) on hover for
                instant brand recognition. Sits right after Instagram so the
                two socials read as a single visual cluster. */}
            <a
              href="https://www.facebook.com/profile.php?id=61591381741161"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Travelsate on Facebook"
              data-testid="footer-facebook-link"
              className="group inline-flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/15 group-hover:border-transparent group-hover:bg-[#1877F2] transition-all">
                <Facebook className="w-3.5 h-3.5" />
              </span>
              <span className="hidden sm:inline text-[11px] tracking-wide">Facebook</span>
            </a>
            {/* YouTube link — YouTube's signature red (#FF0000) on hover.
                Rounds out the social cluster with the third major platform
                travel customers vet before booking. */}
            <a
              href="https://www.youtube.com/@travelsate_letsgo"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Subscribe to Travelsate on YouTube"
              data-testid="footer-youtube-link"
              className="group inline-flex items-center gap-1.5 text-white/60 hover:text-white transition-colors"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/15 group-hover:border-transparent group-hover:bg-[#FF0000] transition-all">
                <Youtube className="w-3.5 h-3.5" />
              </span>
              <span className="hidden sm:inline text-[11px] tracking-wide">YouTube</span>
            </a>
          </nav>
        </div>
      </footer>

      <AuthModal
        open={showAuth}
        onClose={() => { setShowAuth(false); setAuthPurpose(null); }}
        setIsAuthenticated={setIsAuthenticated}
        setUser={setUser}
        onSuccess={handleAuthSuccess}
      />

      <EnquiryModal
        open={showEnquiry}
        onClose={() => setShowEnquiry(false)}
      />
    </div>
  );
}
